import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);
dayjs.extend(timezone);

type DateRangeValidationUnit = 'month' | 'day' | 'week' | 'year';

class TimeService {
    sortDates(dateA: string, dateB: string, order: 'asc' | 'desc' = 'desc') {
        if (!dateA || !dateB) {
            return 0;
        }

        const a = dayjs(dateA);
        const b = dayjs(dateB);

        if (a.valueOf() === b.valueOf()) {
            return 0;
        }

        if (order === 'asc') {
            return a.valueOf() < b.valueOf() ? -1 : 1;
        } else {
            return b.valueOf() < a.valueOf() ? -1 : 1;
        }
    }

    isWithinWindow(date: string, releaseWindow: number = 30): boolean {
        const releaseDate = new Date(date);
        const today = new Date();
        const endDate = new Date(releaseDate.getTime() + releaseWindow * 24 * 60 * 60 * 1000);
        return today <= endDate;
    }

    /**
     * Checks if a date range exceeds a specified limit
     * @param startDate - Start date string
     * @param endDate - End date string
     * @param maxValue - Maximum allowed value
     * @param unit - Unit of measurement ('month', 'day', 'week', 'year')
     * @returns true if the range exceeds the limit, false otherwise
     */
    isDateRangeExceeding(
        startDate: string,
        endDate: string,
        maxValue: number,
        unit: DateRangeValidationUnit = 'month'
    ): boolean {
        if (!startDate || !endDate) {
            return false;
        }

        const start = dayjs(startDate);
        const end = dayjs(endDate);
        const diff = end.diff(start, unit, true);

        return diff > maxValue;
    }

    formatDateWithTimezone(
        dateToFormat: string | null | undefined,
        userTimezone: string
    ): string | null {
        if (!dateToFormat) {
            return null;
        }

        return dayjs.tz(dateToFormat, userTimezone).toISOString();
    }

    /**
     * Check if a date string is empty or nil
     */
    private isEmptyDate(dateStr: string | undefined | null): boolean {
        return !dateStr || dateStr === '';
    }

    /**
     * Format a date string to ISO 8601 format with timezone
     * Used for group API calls
     * @param dateStr - The date string to format
     * @param timezone - The timezone to apply
     * @returns Formatted ISO string or undefined if dateStr is empty/nil
     */
    formatDateForGroup(dateStr: string | undefined | null, timezone: string): string | undefined {
        if (this.isEmptyDate(dateStr) || !timezone) return undefined;
        return dayjs.tz(dateStr, timezone).toISOString();
    }

    /**
     * Format a date string to short date format (YYYY-MM-DD)
     * Used for API calls that expect date-only format
     * @param dateStr - The date string to format
     * @returns Formatted date string (YYYY-MM-DD) or undefined if dateStr is empty/nil
     */
    formatDateForAPI(dateStr: string | undefined | null): string | undefined {
        if (this.isEmptyDate(dateStr)) return undefined;
        return dateStr.slice(0, 10);
    }

    /**
     * Coerce a value that may be a Date or a serialized date (e.g. from JSON.parse) to an ISO 8601 string.
     * Returns null if the value is not a valid date (avoids RangeError from toISOString on Invalid Date).
     */
    ensureAPIDateFormat(value: Date | string | number): string | null {
        const date = value instanceof Date ? value : new Date(value);
        if (!Number.isFinite(date.getTime())) {
            return null;
        }
        return date.toISOString();
    }

    /**
     * Format a date string for Analytics V2 API (ISO 8601 format)
     * Used for V2 analytics endpoints that expect full ISO datetime format
     * @param dateStr - The date string to format
     * @param isEndDate - If true, sets time to end of day (23:59:59.999)
     * @returns Formatted ISO string or null if dateStr is empty/nil
     */
    formatDateForAnalyticsV2(
        dateStr: string | undefined | null,
        isEndDate: boolean = false
    ): string | null {
        if (this.isEmptyDate(dateStr)) return null;

        // Parse all inputs (both ISO and non-ISO) with dayjs.utc
        const date = dayjs.utc(dateStr);
        if (!date.isValid()) return null;

        // Apply startOf/endOf day based on isEndDate flag
        if (isEndDate) {
            return date.endOf('day').toISOString();
        }

        return date.startOf('day').toISOString();
    }
}

const timeService = new TimeService();

export { timeService as TimeService };
